/**
 * Decodes Opus and Vorbis packets (the sound of most WebM files) to mono.
 *
 * WebCodecs' AudioDecoder takes the packets as they are, where the browser
 * has it. Elsewhere the packets are put back into a small audio-only WebM
 * and handed to decodeAudioData, which reads WebM in the browsers that play
 * it. Either way the result is placed by its end, like the other decoders
 * here: a decoder may drop samples at the start (Opus pre-skip, the first
 * Vorbis packet) but never at the end.
 */

import type { ByteSource } from './byteSource';
import { PcmClip, ReadAudioOptions, StreamDecoder, pcmToWav, place, webAudioDecoder } from './audioDecode';
import { MediaReadError, PacketTable, packetRange, packetTime, readPackets } from './isoAudio';
import type { XiphAudioTrack, XiphCodec } from './matroskaAudio';
import { buildAudioWebm } from './webmWriter';

export { buildAudioWebm };

/** Turns a Xiph track's packets into mono samples at the codec's own rate, or null when it can't. */
export type PacketDecoder = (codec: XiphCodec, packets: Uint8Array[], timestampsUs: number[]) => Promise<{ samples: Float32Array; sampleRate: number } | null>;

const BATCH_SECONDS = 60;

// ---------------------------------------------------------------------------
// WebCodecs
// ---------------------------------------------------------------------------

interface AudioDataLike {
  numberOfFrames: number;
  numberOfChannels: number;
  sampleRate: number;
  format: string | null;
  copyTo(destination: Float32Array, options: { planeIndex: number; format?: string }): void;
  allocationSize(options: { planeIndex: number; format?: string }): number;
  close(): void;
}

interface DecoderLike {
  configure(config: object): void;
  decode(chunk: unknown): void;
  flush(): Promise<void>;
  close(): void;
}

interface WebCodecsGlobals {
  AudioDecoder?: {
    new (init: { output: (data: AudioDataLike) => void; error: (error: unknown) => void }): DecoderLike;
    isConfigSupported(config: object): Promise<{ supported?: boolean }>;
  };
  EncodedAudioChunk?: new (init: { type: string; timestamp: number; duration?: number; data: Uint8Array }) => unknown;
}

/** Averages one AudioData's channels into `out` from `at`. */
const mixDown = (data: AudioDataLike): Float32Array => {
  const frames = data.numberOfFrames;
  const channels = Math.max(1, data.numberOfChannels);
  const mono = new Float32Array(frames);
  // Vorbis's first packet decodes to nothing; Firefox hands that over as an empty AudioData.
  if (frames === 0) return mono;
  try {
    const plane = new Float32Array(frames);
    for (let c = 0; c < channels; c += 1) {
      data.copyTo(plane, { planeIndex: c, format: 'f32-planar' });
      for (let i = 0; i < frames; i += 1) mono[i] += plane[i];
    }
  } catch {
    // Some browsers only copy in the decoder's own (interleaved) layout.
    mono.fill(0);
    const interleaved = new Float32Array(frames * channels);
    data.copyTo(interleaved, { planeIndex: 0, format: 'f32' });
    for (let i = 0; i < frames; i += 1) {
      let sum = 0;
      for (let c = 0; c < channels; c += 1) sum += interleaved[i * channels + c];
      mono[i] = sum;
    }
  }
  if (channels > 1) for (let i = 0; i < frames; i += 1) mono[i] /= channels;
  return mono;
};

const join = (parts: Float32Array[]): Float32Array => {
  let total = 0;
  for (let i = 0; i < parts.length; i += 1) total += parts[i].length;
  const out = new Float32Array(total);
  let at = 0;
  for (let i = 0; i < parts.length; i += 1) {
    out.set(parts[i], at);
    at += parts[i].length;
  }
  return out;
};

/** WebCodecs' AudioDecoder, or null when the browser has none for this codec. */
export const webCodecsDecoder: PacketDecoder = async (codec, packets, timestampsUs) => {
  const globals = (typeof window === 'undefined' ? {} : window) as unknown as WebCodecsGlobals;
  const Decoder = globals.AudioDecoder;
  const Chunk = globals.EncodedAudioChunk;
  if (!Decoder || !Chunk) return null;
  const config = { codec: codec.kind, sampleRate: codec.sampleRate, numberOfChannels: codec.channels, description: codec.description };
  try {
    const support = await Decoder.isConfigSupported(config);
    if (!support?.supported) return null;
  } catch {
    return null;
  }
  const parts: Float32Array[] = [];
  let sampleRate = codec.sampleRate;
  let failure: unknown = null;
  const decoder = new Decoder({
    output: (data) => {
      try {
        sampleRate = data.sampleRate || sampleRate;
        parts.push(mixDown(data));
      } catch (error) {
        failure = error;
      } finally {
        data.close();
      }
    },
    error: (error) => {
      failure = error;
    },
  });
  try {
    decoder.configure(config);
    for (let i = 0; i < packets.length; i += 1) {
      decoder.decode(new Chunk({ type: 'key', timestamp: Math.round(timestampsUs[i]), data: packets[i] }));
    }
    await decoder.flush();
  } finally {
    try {
      decoder.close();
    } catch {
      /* already closed after an error */
    }
  }
  if (failure) throw failure;
  return { samples: join(parts), sampleRate };
};

// ---------------------------------------------------------------------------
// Reading a stretch of a Xiph track
// ---------------------------------------------------------------------------

export interface XiphReadOptions extends ReadAudioOptions {
  /** Replaces WebCodecs (tests, or a browser known to lack it). */
  packetDecoder?: PacketDecoder;
}

/** Mono floats at `fromRate` as a 32-bit float WAV, for the stream decoder to resample. */
const floatWav = (samples: Float32Array, fromRate: number): Uint8Array =>
  pcmToWav(
    { kind: 'pcm', bits: 32, float: true, littleEndian: true, unsigned: false, sampleRate: fromRate, channels: 1, bytesPerFrame: 4 },
    new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength)
  );

/**
 * Mono samples for presentation seconds [startS, endS) of an Opus or Vorbis
 * track, at `sampleRate`, read and decoded a minute at a time.
 */
export const readXiphClip = async (
  source: ByteSource,
  track: XiphAudioTrack,
  startS: number,
  endS: number,
  sampleRate: number,
  options: XiphReadOptions = {}
): Promise<PcmClip> => {
  const decoder: StreamDecoder = options.decoder ?? webAudioDecoder;
  const packetDecoder = options.packetDecoder ?? webCodecsDecoder;
  const from = Math.max(0, startS);
  const to = Math.max(from, Math.min(endS, track.durationSeconds));
  const samples = new Float32Array(Math.max(0, Math.round((to - from) * sampleRate)));
  const clip: PcmClip = { samples, sampleRate, startS: from };
  if (samples.length === 0) return clip;
  const table = track.table as PacketTable;
  const asTrack = track as unknown as Parameters<typeof packetRange>[0];
  // Opus needs 80 ms before a point to settle; Vorbis, the packet before.
  const warmUp = track.codec.kind === 'opus' ? 4 : 2;

  const batches: Array<[number, number]> = [];
  for (let at = from; at < to; at += BATCH_SECONDS) batches.push([at, Math.min(to, at + BATCH_SECONDS)]);
  for (let b = 0; b < batches.length; b += 1) {
    if (options.signal?.aborted) {
      const error = new Error('The operation was aborted.');
      error.name = 'AbortError';
      throw error;
    }
    const [batchStart, batchEnd] = batches[b];
    const report = (fraction: number) => options.onProgress?.((b + Math.min(1, fraction)) / batches.length);
    const [first, end] = packetRange(asTrack, batchStart, batchEnd, warmUp);
    if (end <= first) continue;
    const packets = await readPackets(source, asTrack, first, end, {
      concurrency: options.concurrency,
      signal: options.signal,
      onProgress: (done, total) => report((done / total) * 0.7),
    });
    const timestampsUs: number[] = [];
    for (let i = first; i < end; i += 1) {
      if (table.sizes[i] > 0) timestampsUs.push(packetTime(asTrack, i) * 1e6);
    }
    let decoded: Float32Array;
    let direct: { samples: Float32Array; sampleRate: number } | null = null;
    try {
      direct = await packetDecoder(track.codec, packets, timestampsUs);
    } catch (error) {
      // A browser whose WebCodecs refuses these packets may still read them as a WebM.
      if ((error as { name?: string } | null)?.name === 'AbortError') throw error;
      direct = null;
    }
    if (direct) {
      decoded = direct.sampleRate === sampleRate ? direct.samples : await decoder(floatWav(direct.samples, direct.sampleRate), sampleRate);
    } else {
      try {
        decoded = await decoder(buildAudioWebm(track.codec, packets, timestampsUs), sampleRate);
      } catch {
        throw new MediaReadError('unsupported-audio', `This browser can’t decode ${track.codec.kind === 'opus' ? 'Opus' : 'Vorbis'} sound.`);
      }
    }
    const last = end - 1;
    const batchEndTime = packetTime(asTrack, last) + table.durations[last] / track.timescale;
    place(samples, from, decoded, batchEndTime - decoded.length / sampleRate, sampleRate, batchStart, batchEnd);
    report(1);
  }
  return clip;
};
